import { DocumentType } from '@/components/Document/types/document-type';
import { useCustomerDocumentVersionStore } from '@/stores/user-value/customer-document-version';
import { storeToRefs } from 'pinia';
import type { Location, RouteConfig } from 'vue-router';

type DocumentTypeWithPlural = DocumentType | keyof { [key in DocumentType as `${key}s`]: string };

const documentsMap: Record<DocumentTypeWithPlural, string> = {
  // Customers
  quote: 'quotes-list',
  quotes: 'quotes-list',
  contract_note: 'contract-notes-list',
  contract_notes: 'contract-notes-list',
  delivery_note: 'delivery-notes-list',
  delivery_notes: 'delivery-notes-list',
  invoice: 'invoices-list',
  invoices: 'invoices-list',
  credit_note: 'credit-notes-list',
  credit_notes: 'credit-notes-list',
  recurring_invoice: 'recurring-invoices-list',
  recurring_invoices: 'recurring-invoices-list',
  template: 'templates-list',
  templates: 'templates-list',
  supplier_credit_note: 'supplier-credit-notes-list',
  supplier_credit_notes: 'supplier-credit-notes-list',

  // Suppliers
  receipt: 'receipts-list',
  receipts: 'receipts-list',
};

const newDocumentsMap: Record<DocumentTypeWithPlural, string> = {
  // Customers
  quote: 'quotes-new',
  quotes: 'quotes-new',
  contract_note: 'contract-notes-new',
  contract_notes: 'contract-notes-new',
  delivery_note: 'delivery-notes-new',
  delivery_notes: 'delivery-notes-new',
  invoice: 'invoices-new',
  invoices: 'invoices-new',
  credit_note: 'credit-notes-new',
  credit_notes: 'credit-notes-new',
  recurring_invoice: 'recurring-invoices-new',
  recurring_invoices: 'recurring-invoices-new',
  template: 'templates-list',
  templates: 'templates-list',
  supplier_credit_note: 'supplier-credit-notes-list',
  supplier_credit_notes: 'supplier-credit-notes-list',

  // Suppliers
  receipt: 'receipts-list',
  receipts: 'receipts-list',
};

function isNewVersion(documentType: DocumentType) {
  const { entity } = storeToRefs(useCustomerDocumentVersionStore());

  switch (documentType) {
    case DocumentType.CONTRACT_NOTE:
      return entity.value?.useNewContractNoteVersion;
    case DocumentType.CREDIT_NOTE:
      return entity.value?.useNewCreditNoteVersion;
    case DocumentType.DELIVERY_NOTE:
      return entity.value?.useNewDeliveryNoteVersion;
    case DocumentType.INVOICE:
      return entity.value?.useNewInvoiceVersion;
    case DocumentType.QUOTE:
      return entity.value?.useNewQuoteVersion;
    case DocumentType.RECURRING_INVOICE:
      return entity.value?.useNewRecurringInvoiceVersion;

    default:
      return false;
  }
}

export function getDocumentsRoute(
  documentType: DocumentType = DocumentType.INVOICE,
  group = 'open',
  query: Location['query'] = undefined,
): Location {
  return {
    name: isNewVersion(documentType) ? newDocumentsMap[documentType] : documentsMap[documentType],
    params: { group },
    query,
  };
}

const documentRoutes: Array<RouteConfig> = [
  {
    path: '/documents/:type/:group?',
    name: 'documents',
    beforeEnter(to, _, next) {
      next(getDocumentsRoute(to.params.type as DocumentType, to.params.group, to.query));
    },
  },
  {
    path: '/document/:documentUuid',
    name: 'document-resolve',
    component: () => import('@/pages/documents/DocumentResolver.vue'),
    props: true,
  },
];

export default documentRoutes;
